import { LineItemTypes, StepTypes } from '@parallel-fluidics/shared/constants';
import { calculateBusinessDays, formatDate } from '@parallel-fluidics/shared/shipping';
import * as Sentry from '@sentry/react';
import PropTypes from 'prop-types';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { groupIntoTreesByLevels } from 'utils/reactFlowUtils';

const TrackersDataContext = createContext();

export default function TrackersProvider({ children, orders, isShipped = false }) {
  const [collapsedView, setCollapsedView] = useState(localStorage.getItem('collapsedView') ?? 'list');

  useEffect(() => {
    localStorage.setItem('collapsedView', collapsedView);
  }, [collapsedView]);

  const {
    groupedByProject: processedOrders,
    sortedGroups: renderGroups,
    productLineItems,
  } = useMemo(() => {
    const groupedByProject = {};
    const productRows = [];
    (orders || []).forEach((order) => {
      (order?.shipments || []).forEach((shipment) => {
        (shipment?.lineItems || []).forEach((lineItem) => {
          const lastTrackingData = shipment.trackingData?.[shipment.trackingData.length - 1];
          const trackShipDate = lastTrackingData?.shipDate
            ? formatDate(lastTrackingData.shipDate)
            : null;

          const shipDateActual = shipment.shipDate || trackShipDate;
          const businessDays = calculateBusinessDays(shipDateActual ?? shipment.targetShipDate);

          // Collect product-type line items into a separate flat list
          if (lineItem.type === LineItemTypes.PRODUCT) {
            productRows.push({
              orderId: order.id,
              orgId: order.organization,
              lineItemId: lineItem.id,
              notes: lineItem.notes,
              sku: lineItem.sku,
              productDocId: lineItem.productDocId,
              productVersionId: lineItem.productVersionId,
              projectId: lineItem.projectId,
              pPartId: lineItem.pPartId,
              required: lineItem.quantity || 0,
              shipped: lineItem.quantityShipped || 0,
              targetShipDate: shipment.targetShipDate,
              shipDateActual,
              shipDateBusinessDays: businessDays,
            });
            return;
          }

          const { projectId, projectDocId } = lineItem;
          if (!projectId) {
            Sentry.captureMessage('Line item is missing projectId', {
              level: 'warning',
              extra: {
                orderId: order.id,
                lineItemId: lineItem.id,
                projectDocId,
                lineItem,
              },
            });
            return; // guard against malformed data
          }

          if (!groupedByProject[projectId]) {
            groupedByProject[projectId] = {
              lineItemsMap: {},
              stepsArr: [],
              projectDocId,
            };
          }

          const lineItemSteps = (lineItem.steps || []).map((step) => ({
            ...step,
            // add additional lineItem data to each step for easier tracking
            projectId: lineItem.projectId,
            projectDocId: lineItem.projectDocId,
            lineItemId: lineItem.id,
            targetPPartId: lineItem.pPartId,
          }));

          // Allow manual traveler linking if first step isn't molding or bonding
          const canAddTravelers = lineItemSteps[0]?.type
            && lineItemSteps[0]?.type !== StepTypes.MOLDING
            && lineItemSteps[0]?.type !== StepTypes.BONDING;
          groupedByProject[projectId].lineItemsMap[lineItem.id] = {
            lineItemId: lineItem.id,
            projectId: lineItem.projectId,
            pPartId: lineItem.pPartId,
            goal: lineItem.quantity,
            shipDateTarget: shipment.targetShipDate,
            shipDateActual,
            shipDateBusinessDays: businessDays,
            notes: lineItem.notes,
            canAddTravelers,
            // only include steps if canAddTravelers is true to save memory
            steps: canAddTravelers ? lineItemSteps : [],
          };
          groupedByProject[projectId].orgId = order.organization;
          groupedByProject[projectId].mostRecentShipDate = Math.max(
            groupedByProject[projectId].mostRecentShipDate || 0,
            new Date(shipDateActual || shipment.targetShipDate).getTime(),
          );
          groupedByProject[projectId].stepsArr.push(lineItemSteps);
        });
      });
    });

    const sortedGroups = Object.entries(groupedByProject)
      .sort(([, a], [, b]) => (
        isShipped
          ? b.mostRecentShipDate - a.mostRecentShipDate
          : a.mostRecentShipDate - b.mostRecentShipDate
      ))
      .map(([projectId, {
        orgId,
        stepsArr,
      }]) => {
        const trees = groupIntoTreesByLevels(stepsArr);
        // Remove stepsArr from groupedByProject since it's no longer needed
        delete groupedByProject[projectId].stepsArr;
        return {
          projectId,
          trees,
          orgId,
        };
      });
    return { groupedByProject, sortedGroups, productLineItems: productRows };
  }, [orders, isShipped]);

  const value = useMemo(() => ({
    collapsedView,
    setCollapsedView,
    renderGroups,
    groupedOrdersByProject: processedOrders,
    productLineItems,
  }), [collapsedView, renderGroups, processedOrders, productLineItems]);

  return (
    <TrackersDataContext.Provider value={value}>
      {children}
    </TrackersDataContext.Provider>
  );
}
TrackersProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  orders: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  isShipped: PropTypes.bool,
};

export const useTrackersData = () => {
  const context = useContext(TrackersDataContext);
  if (context === undefined) {
    throw new Error('useTrackersData must be used within a TrackersProvider');
  }
  return context;
};
